import type { OTCDealData } from '@src/types/OTC';
import { DayCountConventionEnum, DealStatusEnum, formatExtendedDurationValue, SubProductTypeEnum } from '@talos/kyoko';

/**
 * Available underlying symbols for OTC Forward and Option products.
 * Index selection is driven by fixingIndexMapping / usePriceSourceFixingsByFullSymbol.
 */
export const AVAILABLE_UNDERLYING_SYMBOLS = ['BTC', 'ETH'] as const;

export type AvailableUnderlyingSymbol = (typeof AVAILABLE_UNDERLYING_SYMBOLS)[number];

/** Type guard for runtime checks */
export function isAvailableUnderlyingSymbol(s: string): s is AvailableUnderlyingSymbol {
  return (AVAILABLE_UNDERLYING_SYMBOLS as readonly string[]).includes(s);
}

/**
 * Statuses that allow a deal to be modified
 */
export const MODIFIABLE_DEAL_STATUSES: DealStatusEnum[] = [
  DealStatusEnum.New,
  DealStatusEnum.Pending,
  DealStatusEnum.Live,
];

/**
 * Statuses that allow a deal to be canceled
 */
export const CANCELABLE_DEAL_STATUSES: DealStatusEnum[] = [
  DealStatusEnum.New,
  DealStatusEnum.Pending,
  DealStatusEnum.Live,
];

/**
 * Check if a deal can be modified based on its status
 */
export function isDealModifiable(deal: OTCDealData): boolean {
  return MODIFIABLE_DEAL_STATUSES.includes(deal.Status);
}

/**
 * Check if a deal can be canceled based on its status
 */
export function isDealCancelable(deal: OTCDealData): boolean {
  return CANCELABLE_DEAL_STATUSES.includes(deal.Status);
}

/**
 * Pending deals can be activated via POST /contracts/deals/{id}/activate
 */
export function isDealActivatable(deal: OTCDealData): boolean {
  return deal.Status === DealStatusEnum.Pending;
}

/**
 * Format settlement cycle with prefix (e.g., "T + 2 Days")
 */
export function formatSettlementCycle(settleCycle: unknown): string {
  if (typeof settleCycle !== 'string' || !settleCycle) {
    return '';
  }
  return formatExtendedDurationValue(settleCycle, 'T +');
}

/**
 * Format holiday calendars as comma-separated string
 */
export function formatHolidayCalendars(
  holidayCalendars: OTCDealData['DealInfo']['HolidayCalendars'] | undefined
): string {
  if (!holidayCalendars || holidayCalendars.length === 0) {
    return '';
  }
  return holidayCalendars.map(calendar => calendar.Name).join(', ');
}

/**
 * Format product type based on SubProductType
 */
export function formatProductType(subProductType: unknown): string {
  if (typeof subProductType !== 'string') {
    return String(subProductType ?? '');
  }
  switch (subProductType) {
    case SubProductTypeEnum.OTCVanillaOption:
      return 'Vanilla Option';
    case SubProductTypeEnum.OTCFloatingRateLoan:
      return 'Floating Rate Loan';
    case SubProductTypeEnum.OTCFixedRateLoan:
      return 'Fixed Rate Loan';
    case SubProductTypeEnum.OTCForward:
      return 'Forward';
    default:
      return subProductType;
  }
}

/**
 * Format payment period enum to readable text
 */
export function formatPaymentPeriod(paymentPeriod: unknown): string {
  if (typeof paymentPeriod !== 'string' || !paymentPeriod) {
    return '';
  }
  switch (paymentPeriod) {
    case '1D':
      return 'Daily';
    case '1W':
      return 'Weekly';
    case '1M':
      return 'Monthly';
    case '1Y':
      return 'Yearly';
    default:
      return paymentPeriod;
  }
}

/**
 * Format day count convention code to short display text (aligned with ref-data labels where applicable)
 */
export function formatDayCountConvention(dayCount: unknown): string {
  if (typeof dayCount !== 'string' || !dayCount) {
    return '';
  }
  switch (dayCount) {
    case DayCountConventionEnum.Thirty360:
      return '30/360';
    case DayCountConventionEnum.Actual360:
      return 'Actual/360';
    case DayCountConventionEnum.Actual365:
      return 'Actual/365';
    case DayCountConventionEnum.Actual365f:
      return 'Actual/365F';
    case DayCountConventionEnum.ActualActual:
      return 'Actual/Actual';
    case DayCountConventionEnum.ActualActualTreasury:
      return 'Actual/Actual (Treasury)';
    default:
      return dayCount;
  }
}
